import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchChangeCalendar, fetchChanges } from '@/lib/api';
import { Link } from 'react-router-dom';
import { StatusDot } from '@/components/shared/StatusDot';
import { ScrollArea } from '@/components/ui/scroll-area';
import { GitBranch, Calendar, ChevronRight, Shield, CheckCircle, XCircle, Clock, Users, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const RISK_COLOR = (score) => {
  if (!score) return 'text-[var(--nexus-text-muted)]';
  if (score < 20) return 'text-[var(--nexus-success)]';
  if (score < 50) return 'text-[var(--nexus-warning)]';
  return 'text-[var(--nexus-danger)]';
};
const RISK_LABEL = (score) => {
  if (!score) return 'Not scored';
  if (score < 20) return 'Auto-approve';
  if (score < 50) return 'CAB Review';
  if (score < 75) return 'Senior Review';
  return 'Blocked';
};
const ROUTE_COLORS = {
  'AUTO_APPROVE': 'bg-[#0d3d1d] text-green-300 border-green-500/30',
  'STANDARD_CAB': 'bg-[#4d3000] text-[#fcd34d] border-amber-500/30',
  'SENIOR_REVIEW': 'bg-[#4a1515] text-red-300 border-red-500/30',
  'BLOCKED': 'bg-[#4a1515] text-red-300 border-red-500/30',
};

export default function ChangesPage() {
  const token = localStorage.getItem('nexus_token');
  const queryClient = useQueryClient();
  const [approvalPanel, setApprovalPanel] = useState(null);

  const { data: calendar, isLoading: loadingCal } = useQuery({ queryKey: ['change-calendar'], queryFn: fetchChangeCalendar, refetchInterval: 30000 });
  const { data: changes, isLoading } = useQuery({ queryKey: ['changes'], queryFn: () => fetchChanges(), refetchInterval: 15000 });
  const { data: windows } = useQuery({
    queryKey: ['maintenance-windows'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/changes/maintenance-windows`, { headers: { Authorization: `Bearer ${token}` } }); return data; },
  });

  const approveMutation = useMutation({
    mutationFn: async ({ changeId, role }) => {
      const { data } = await axios.post(`${BACKEND_URL}/api/changes/${changeId}/approve`, { approver_role: role }, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: (data) => {
      if (data.status === 'executing') {
        toast.success('All approvals collected — CHANGE-IQ is executing the change');
      } else {
        toast.success(`Approval recorded (${data.approvals}). Waiting for: ${data.pending?.join(', ')}`);
      }
      queryClient.invalidateQueries(['changes', 'change-calendar']);
    },
    onError: () => toast.error('Approval failed'),
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ changeId }) => {
      const { data } = await axios.post(`${BACKEND_URL}/api/tickets/${changeId}/override`, { action: 'close' }, { headers: { Authorization: `Bearer ${token}` } });
      return data;
    },
    onSuccess: () => { toast.success('Change rejected and closed'); queryClient.invalidateQueries(['changes']); },
  });

  // Separate pending approvals from others
  const pendingApproval = (changes || []).filter(c => c.status === 'PENDING_APPROVAL');
  const sortedDates = calendar ? Object.keys(calendar).sort() : [];

  return (
    <div data-testid="changes-page" className="w-full max-w-[1600px] mx-auto">
      <div className="mb-4">
        <h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)] tracking-tight">Change Management</h1>
        <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">CHANGE-IQ risk analysis, CAB approval workflow, and change calendar</p>
      </div>

      {/* CAB Approval Queue */}
      {pendingApproval.length > 0 && (
        <div className="nexus-card relative overflow-hidden mb-4" data-testid="cab-approval-queue">
          <div className="p-3 border-b border-[var(--nexus-border)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-[var(--nexus-warning)]" />
                <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">CAB Approval Queue</h2>
                <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#4d3000] text-[#fcd34d] border border-amber-500/30">
                  {pendingApproval.length} PENDING
                </span>
              </div>
              {windows?.current && (
                <span className="text-[10px] text-[var(--nexus-text-muted)]">
                  {windows.current.in_window ? '🟢 In maintenance window' : `⏳ ${windows.current.message}`}
                </span>
              )}
            </div>
          </div>
          <div className="p-3 space-y-2" data-testid="pending-changes">
            {pendingApproval.map(change => {
              const meta = change.change_metadata || {};
              const route = meta.route || 'UNKNOWN';
              const approversNeeded = meta.approvers_needed || [];
              const approversReceived = meta.approvers_received || [];
              const isExpanded = approvalPanel === change.id;
              const routeStyle = ROUTE_COLORS[route] || 'bg-[var(--nexus-bg-base)] text-[var(--nexus-text-muted)]';

              return (
                <div key={change.id} className="rounded-xl bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] overflow-hidden">
                  {/* Change header */}
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded badge-${change.priority?.toLowerCase()}`}>{change.priority}</span>
                      <Link to={`/incidents/${change.id}`} className="font-mono text-xs text-[var(--nexus-primary-bright)] hover:underline">{change.id}</Link>
                      <span className="text-xs text-[var(--nexus-text-primary)] truncate">{change.title}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Risk score */}
                      <span className={`font-mono text-[10px] font-bold ${RISK_COLOR(change.risk_score)}`}>{change.risk_score || '?'}/100</span>
                      {/* Route badge */}
                      <span className={`font-mono text-[10px] font-semibold px-2 py-0.5 rounded border ${routeStyle}`}>{route.replace('_', ' ')}</span>
                      {/* Expand */}
                      <button onClick={() => setApprovalPanel(isExpanded ? null : change.id)}
                        className="text-xs text-[var(--nexus-accent)] hover:underline" data-testid={`expand-${change.id}`}>
                        {isExpanded ? 'Collapse' : 'Review'}
                      </button>
                    </div>
                  </div>

                  {/* Approval panel (expanded) */}
                  {isExpanded && (
                    <div className="p-3 border-t border-[var(--nexus-border)] bg-[var(--nexus-bg-elevated)] animate-nexus-slide-in" data-testid={`approval-panel-${change.id}`}>
                      {/* Risk factors */}
                      {meta.risk?.factors && (
                        <div className="mb-3">
                          <h4 className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-1.5">Risk Assessment</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                            {Object.entries(meta.risk.factors).map(([key, val]) => (
                              <div key={key} className="p-1.5 rounded bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                                <div className="flex items-center justify-between">
                                  <span className="text-[9px] text-[var(--nexus-text-muted)]">{key.replace(/_/g, ' ')}</span>
                                  <span className={`font-mono text-[10px] font-bold ${val.score > 60 ? 'text-[var(--nexus-danger)]' : val.score > 30 ? 'text-[var(--nexus-warning)]' : 'text-[var(--nexus-success)]'}`}>{val.score}</span>
                                </div>
                                <p className="text-[9px] text-[var(--nexus-text-muted)] mt-0.5 truncate">{val.detail}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Conflicts */}
                      {meta.ci_conflicts?.length > 0 && (
                        <div className="mb-3 p-2 rounded bg-[#4d3000]/30 border border-amber-500/20">
                          <div className="flex items-center gap-1.5">
                            <AlertTriangle size={12} className="text-[var(--nexus-warning)]" />
                            <span className="text-[10px] font-semibold text-[var(--nexus-warning)]">CI Conflicts: {meta.ci_conflicts.map(c => c.change_id).join(', ')}</span>
                          </div>
                        </div>
                      )}

                      {/* Impact */}
                      {meta.impact && (
                        <div className="mb-3 flex items-center gap-4 text-[10px] text-[var(--nexus-text-muted)]">
                          <span>Downstream: <strong className="text-[var(--nexus-text-secondary)]">{meta.impact.downstream}</strong></span>
                          <span>Upstream: <strong className="text-[var(--nexus-text-secondary)]">{meta.impact.upstream}</strong></span>
                          <span>Affected CIs: <strong className="text-[var(--nexus-text-secondary)]">{change.affected_cis?.length || 0}</strong></span>
                        </div>
                      )}

                      {/* Approval progress */}
                      <div className="mb-3">
                        <h4 className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-1.5">
                          Approvals ({approversReceived.length}/{approversNeeded.length})
                        </h4>
                        <div className="flex items-center gap-2">
                          {approversNeeded.map(role => {
                            const approved = approversReceived.includes(role);
                            return (
                              <div key={role} className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono border ${
                                approved ? 'bg-[#0d3d1d] text-green-300 border-green-500/30' : 'bg-[var(--nexus-bg-base)] text-[var(--nexus-text-muted)] border-[var(--nexus-border)]'
                              }`}>
                                {approved ? <CheckCircle size={10} /> : <Clock size={10} />}
                                {role.replace('_', ' ')}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Approval actions */}
                      <div className="flex items-center gap-2">
                        {approversNeeded.filter(r => !approversReceived.includes(r)).map(role => (
                          <button key={role} onClick={() => approveMutation.mutate({ changeId: change.id, role })}
                            disabled={approveMutation.isPending}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0d3d1d] border border-green-500/30 text-green-300 text-xs font-medium hover:bg-green-500/20 transition-colors disabled:opacity-50"
                            data-testid={`approve-${change.id}-${role}`}>
                            <CheckCircle size={12} /> Approve as {role.replace('_', ' ')}
                          </button>
                        ))}
                        <button onClick={() => rejectMutation.mutate({ changeId: change.id })}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#4a1515] border border-red-500/30 text-red-300 text-xs font-medium hover:bg-red-500/20 transition-colors"
                          data-testid={`reject-${change.id}`}>
                          <XCircle size={12} /> Reject
                        </button>
                        <Link to={`/incidents/${change.id}`} className="text-[10px] text-[var(--nexus-accent)] hover:underline ml-auto">
                          View Full Details →
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Change Calendar */}
        <div className="lg:col-span-2">
          <div className="nexus-card relative overflow-hidden">
            <div className="p-3 border-b border-[var(--nexus-border)]">
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-[var(--nexus-warning)]" />
                <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)]">Change Calendar</h2>
              </div>
            </div>
            <ScrollArea className="h-[450px]">
              <div className="p-3 space-y-4" data-testid="change-calendar">
                {loadingCal ? (
                  <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-lg bg-[var(--nexus-bg-base)] animate-pulse" />)}</div>
                ) : sortedDates.length > 0 ? sortedDates.map(date => (
                  <div key={date}>
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={12} className="text-[var(--nexus-text-muted)]" />
                      <span className="font-mono text-[10px] text-[var(--nexus-text-secondary)] uppercase tracking-wider">
                        {date === 'Unscheduled' ? 'Unscheduled' : new Date(date + 'T00:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[var(--nexus-bg-elevated)] text-[var(--nexus-text-muted)] border border-[var(--nexus-border)]">{calendar[date].length}</span>
                    </div>
                    <div className="space-y-1.5 ml-5">
                      {calendar[date].map(change => (
                        <Link key={change.id} to={`/incidents/${change.id}`}
                          className="flex items-center justify-between p-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] hover:bg-white/[0.03] transition-colors group">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded badge-${change.priority?.toLowerCase()}`}>{change.priority}</span>
                            <div className="min-w-0">
                              <span className="font-mono text-[10px] text-[var(--nexus-primary-bright)]">{change.id}</span>
                              <p className="text-xs text-[var(--nexus-text-primary)] truncate">{change.title}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="text-right">
                              <span className={`font-mono text-[10px] font-bold ${RISK_COLOR(change.risk_score)}`}>{change.risk_score || '-'}/100</span>
                              <p className={`text-[9px] ${RISK_COLOR(change.risk_score)}`}>{RISK_LABEL(change.risk_score)}</p>
                            </div>
                            <StatusDot status={change.status === 'RESOLVED' ? 'success' : change.status === 'PENDING_APPROVAL' ? 'warning' : 'processing'} size="sm" />
                            <ChevronRight size={12} className="text-[var(--nexus-text-muted)] opacity-0 group-hover:opacity-100" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-[var(--nexus-text-muted)] text-xs">No changes scheduled</div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Right sidebar */}
        <div className="space-y-3">
          {/* Risk Summary */}
          <div className="nexus-card relative overflow-hidden p-4">
            <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-3 flex items-center gap-2"><Shield size={14} className="text-[var(--nexus-warning)]" /> Risk Summary</h3>
            <div className="space-y-2">
              {[{label: 'Auto-approve (<20)', range: [0,20], color: 'var(--nexus-success)'}, {label: 'CAB Review (20-50)', range: [20,50], color: 'var(--nexus-warning)'}, {label: 'Senior Review (50-75)', range: [50,75], color: 'var(--nexus-danger)'}, {label: 'Blocked (>75)', range: [75,101], color: 'var(--nexus-danger)'}].map(({label, range, color}) => {
                const count = (changes || []).filter(c => (c.risk_score || 0) >= range[0] && (c.risk_score || 0) < range[1]).length;
                return (
                  <div key={label} className="flex items-center justify-between p-2 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
                    <span className="text-[11px] text-[var(--nexus-text-secondary)]">{label}</span>
                    <span className="font-mono text-sm font-semibold tabular-nums" style={{ color }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Maintenance Windows */}
          <div className="nexus-card relative overflow-hidden p-4">
            <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2 flex items-center gap-2"><Clock size={14} className="text-[var(--nexus-accent)]" /> Maintenance Windows</h3>
            {windows?.current && (
              <div className={`p-2 rounded-lg border mb-2 ${windows.current.in_window ? 'bg-[#0d3d1d] border-green-500/20' : 'bg-[var(--nexus-bg-base)] border-[var(--nexus-border)]'}`}>
                <span className={`text-[10px] font-semibold ${windows.current.in_window ? 'text-[var(--nexus-success)]' : 'text-[var(--nexus-text-muted)]'}`}>
                  {windows.current.in_window ? '🟢 Currently in maintenance window' : `⏳ ${windows.current.message}`}
                </span>
              </div>
            )}
            <div className="space-y-1">
              {(windows?.windows || []).map((w, i) => (
                <div key={i} className="flex items-center justify-between text-[10px]">
                  <span className="text-[var(--nexus-text-secondary)]">{w.day}</span>
                  <span className="font-mono text-[var(--nexus-text-muted)]">{w.start}-{w.end} {w.timezone}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Change Stats */}
          <div className="nexus-card relative overflow-hidden p-4">
            <h3 className="text-sm font-semibold text-[var(--nexus-text-primary)] mb-2">Change Stats</h3>
            <div className="space-y-2">
              <div className="flex justify-between"><span className="text-[11px] text-[var(--nexus-text-muted)]">Total</span><span className="font-mono text-xs text-[var(--nexus-text-primary)]">{(changes || []).length}</span></div>
              <div className="flex justify-between"><span className="text-[11px] text-[var(--nexus-text-muted)]">Pending Approval</span><span className="font-mono text-xs text-[var(--nexus-warning)]">{pendingApproval.length}</span></div>
              <div className="flex justify-between"><span className="text-[11px] text-[var(--nexus-text-muted)]">Resolved</span><span className="font-mono text-xs text-[var(--nexus-success)]">{(changes || []).filter(c => c.status === 'RESOLVED').length}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
